export const getBusinessIdentifier = (): string => {
    // First check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const urlIdentifier = urlParams.get('business_identifier');

    if (urlIdentifier) {
        // Store URL identifier in localStorage and return it
        localStorage.setItem('business_identifier', urlIdentifier);
        return urlIdentifier;
    }

    // If not in URL, check localStorage
    const storedIdentifier = localStorage.getItem('business_identifier');

    // Return stored identifier or default to 'convrse' if you want a default
    // Or return empty string or handle appropriately if required
    return storedIdentifier || '';
};

// Business UUID for the logged-in user (stored on the `user` object in
// localStorage, e.g. user.business_id). Falls back to the business_identifier
// slug if absent. Used for APIs that require the business UUID (e.g. recordings).
export const getUserBusinessId = (): string => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user.business_id) return user.business_id;
        if (user.businessId) return user.businessId;
    } catch {
        // ignore malformed user JSON
    }
    return getBusinessIdentifier();
};

// Add a new function to explicitly set the business identifier
export const setBusinessIdentifier = (identifier: string): void => {
    localStorage.setItem('business_identifier', identifier);
};

// Function to clear the business identifier (for switching business contexts)
export const clearBusinessIdentifier = (): void => {
    localStorage.removeItem('business_identifier');
};

// Function to reset all business-related data (for admin switching contexts)
export const resetBusinessContext = (): void => {
    localStorage.removeItem('business_identifier');
    localStorage.removeItem('available_projects');
};

export const getProjectId = (): string | null => {
    // First check localStorage
    const storedIdentifier = localStorage.getItem('business_identifier');
    if (storedIdentifier) {
        return storedIdentifier;
    }

    // If not in localStorage, check URL params
    // This part might be redundant if getBusinessIdentifier is always called first
    const urlParams = new URLSearchParams(window.location.search);
    const businessIdentifier = urlParams.get('business_identifier');
    return businessIdentifier ? businessIdentifier : null;
};

// New function to get available projects for the user
export const getAvailableProjects = (): string[] => {
    // Check if there's a stored list of available projects
    const storedProjects = localStorage.getItem('available_projects');
    if (storedProjects) {
        try {
            const projects = JSON.parse(storedProjects);
            return Array.isArray(projects) ? projects : [];
        } catch (error) {
            console.error('[getAvailableProjects] Error parsing stored projects:', error);
        }
    }

    // If no stored projects, check if we have a single business_identifier
    const businessIdentifier = getBusinessIdentifier();
    if (businessIdentifier) {
        // Return the business identifier as the only available project
        return [businessIdentifier];
    }

    return [];
};

// Function to check if user has single project access
export const hasSingleProjectAccess = (): boolean => {
    const availableProjects = getAvailableProjects();
    const isSingleProject = availableProjects.length === 1;
    return isSingleProject;
};

// Function to get the single project name if user has single project access
export const getSingleProjectName = (): string | null => {
    if (hasSingleProjectAccess()) {
        const projects = getAvailableProjects();
        return projects[0] || null;
    }
    return null;
};

// Function to set available projects (useful when fetched from API)
export const setAvailableProjects = (projects: string[]): void => {
    localStorage.setItem('available_projects', JSON.stringify(projects));
}; 